<script setup lang="ts">
import type { FormkitCommonProps } from "@/components/ui/input/formkit/formkit.types";
import type { THeatPumpModelDto } from "@/types/backoffice/dto/heatpump/heatpump-model-dto.ts";
import type { HeatPumpBrandId } from "@/types/common/ids";
import type { FormKitFrameworkContext } from "@formkit/core";
/**
 * @deprecated Use non-formkit-specific HeatpumpModelAutocompleteInput instead
 */
import type { AutoCompleteCompleteEvent, AutoCompleteItemSelectEvent } from "primevue/autocomplete";
import type { PropType } from "vue";

import { useMutation } from "@tanstack/vue-query";
import { ref } from "vue";

import BackofficeApi from "@/api/backoffice/backoffice-api.ts";
import { HeatPumpQueryKeys } from "@/api/backoffice/heatpump/heatpump-query-keys.ts";
import { DATA_TEST_IDS } from "@/constants/data-test-ids";
import { useFormkitComputedValue } from "@/hooks/common/use-formkit";

import AutoComplete from "primevue/autocomplete";

export interface FormKitHeatpumpModelProps extends FormkitCommonProps {
  brandId: HeatPumpBrandId;
}

const props = defineProps({
  context: {
    type: Object as PropType<FormKitFrameworkContext<THeatPumpModelDto | string> & FormKitHeatpumpModelProps>,
  },
});

const suggestions = ref<Array<THeatPumpModelDto>>([]);

const { mutateAsync: findModels } = useMutation({
  mutationKey: HeatPumpQueryKeys.findModels(props.context?.brandId),
  mutationFn: async (query: string): Promise<THeatPumpModelDto[]> => {
    const brandId = props.context?.brandId;

    if (!brandId) {
      return Promise.resolve([]);
    }

    return BackofficeApi.findHeatPumpModels({ queries: { brandId, query } });
  },
});

const value = useFormkitComputedValue<THeatPumpModelDto | string | null>(props.context);

const search = async (event: AutoCompleteCompleteEvent) => {
  try {
    suggestions.value = await findModels(event.query);
  } catch (e) {
    console.error(e);
  }
};
</script>

<template>
  <AutoComplete
    v-model="value"
    :suggestions="suggestions"
    option-label="model"
    :name="props.context?.name"
    :placeholder="props.context?.placeholder"
    :disabled="props.context?.disabled"
    class="w-full"
    input-class="w-full"
    dropdown
    v-bind="$attrs"
    :data-test-id="DATA_TEST_IDS.components.heatpumpModelAutocomplete.input()"
    @complete="search"
    @item-select="(e: AutoCompleteItemSelectEvent) => (value = e.value)"
  />
</template>

<style scoped></style>
